import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Types for voice calls (based on VoiceCallsListSimple)
export interface VoiceCallRow {
  id: string;
  customer_name?: string;
  customer_number?: string;
  phone_number?: string;
  duration_seconds?: number;
  total_cost?: number;
  call_status?: string;
  status?: string;
  call_type?: string;
  end_reason?: string;
  summary?: string;
  transcript?: string;
  recording_url?: string;
  started_at?: string;
  ended_at?: string;
  created_at: string;
  transfer_destination?: string;
  vapi_call_id: string;
  voice_agent_id: string;
  extracted_sentiment?: string;
  extracted_priority?: string;
  intent?: string;
  next_step?: string;
  tags?: string[];
  voice_agents?: {
    id: string;
    title: string;
    agent_name: string;
    organization_id: string;
  };
}

export interface VoiceAgent {
  id: string;
  agent_name: string;
  title: string;
}

export interface VoiceCallsState {
  // Voice Calls
  calls: VoiceCallRow[];
  isLoadingCalls: boolean;
  callsError: Error | null;
  callsLastFetch: number | null;

  // Voice Agents (for filtering)
  voiceAgents: VoiceAgent[];
  isLoadingAgents: boolean;
  agentsError: Error | null;
  agentsLastFetch: number | null;

  // Global state
  isInitialized: boolean;
  organizationId: string | null;
}

export interface VoiceCallsContextType extends VoiceCallsState {
  // Actions
  refreshCalls: () => Promise<void>;
  refreshAgents: () => Promise<void>;
  refreshAll: () => Promise<void>;
  invalidateCalls: () => void;
  invalidateAgents: () => void;
  invalidateAll: () => void;
  reset: () => void;

  // Computed properties
  isLoading: boolean;
  hasError: boolean;
}

// Action types
type VoiceCallsAction =
  | { type: "RESET" }
  | { type: "SET_ORGANIZATION"; payload: string | null }
  | { type: "CALLS_LOADING"; payload: boolean }
  | { type: "CALLS_SUCCESS"; payload: VoiceCallRow[] }
  | { type: "CALLS_ERROR"; payload: Error }
  | { type: "AGENTS_LOADING"; payload: boolean }
  | { type: "AGENTS_SUCCESS"; payload: VoiceAgent[] }
  | { type: "AGENTS_ERROR"; payload: Error }
  | { type: "INVALIDATE_CALLS" }
  | { type: "INVALIDATE_AGENTS" }
  | { type: "INVALIDATE_ALL" }
  | { type: "SET_INITIALIZED"; payload: boolean };

// Default state
const createDefaultState = (
  organizationId: string | null = null,
): VoiceCallsState => ({
  calls: [],
  isLoadingCalls: false,
  callsError: null,
  callsLastFetch: null,

  voiceAgents: [],
  isLoadingAgents: false,
  agentsError: null,
  agentsLastFetch: null,

  isInitialized: false,
  organizationId,
});

// Reducer
function voiceCallsReducer(
  state: VoiceCallsState,
  action: VoiceCallsAction,
): VoiceCallsState {
  switch (action.type) {
    case "RESET":
      return createDefaultState(state.organizationId);

    case "SET_ORGANIZATION":
      if (state.organizationId === action.payload) return state;
      return createDefaultState(action.payload);

    case "CALLS_LOADING":
      return {
        ...state,
        isLoadingCalls: action.payload,
        callsError: action.payload ? null : state.callsError,
      };

    case "CALLS_SUCCESS":
      return {
        ...state,
        calls: action.payload,
        isLoadingCalls: false,
        callsError: null,
        callsLastFetch: Date.now(),
      };

    case "CALLS_ERROR":
      return {
        ...state,
        isLoadingCalls: false,
        callsError: action.payload,
      };

    case "AGENTS_LOADING":
      return {
        ...state,
        isLoadingAgents: action.payload,
        agentsError: action.payload ? null : state.agentsError,
      };

    case "AGENTS_SUCCESS":
      return {
        ...state,
        voiceAgents: action.payload,
        isLoadingAgents: false,
        agentsError: null,
        agentsLastFetch: Date.now(),
      };

    case "AGENTS_ERROR":
      return {
        ...state,
        isLoadingAgents: false,
        agentsError: action.payload,
      };

    case "INVALIDATE_CALLS":
      return {
        ...state,
        callsLastFetch: null,
      };

    case "INVALIDATE_AGENTS":
      return {
        ...state,
        agentsLastFetch: null,
      };

    case "INVALIDATE_ALL":
      return {
        ...state,
        callsLastFetch: null,
        agentsLastFetch: null,
      };

    case "SET_INITIALIZED":
      return {
        ...state,
        isInitialized: action.payload,
      };

    default:
      return state;
  }
}

// Default context value
const createDefaultContextValue = (): VoiceCallsContextType => ({
  ...createDefaultState(),
  refreshCalls: async () => {
    throw new Error(
      "VoiceCallsContext: refreshCalls called outside of VoiceCallsProvider",
    );
  },
  refreshAgents: async () => {
    throw new Error(
      "VoiceCallsContext: refreshAgents called outside of VoiceCallsProvider",
    );
  },
  refreshAll: async () => {
    throw new Error(
      "VoiceCallsContext: refreshAll called outside of VoiceCallsProvider",
    );
  },
  invalidateCalls: () => {
    throw new Error(
      "VoiceCallsContext: invalidateCalls called outside of VoiceCallsProvider",
    );
  },
  invalidateAgents: () => {
    throw new Error(
      "VoiceCallsContext: invalidateAgents called outside of VoiceCallsProvider",
    );
  },
  invalidateAll: () => {
    throw new Error(
      "VoiceCallsContext: invalidateAll called outside of VoiceCallsProvider",
    );
  },
  reset: () => {
    throw new Error(
      "VoiceCallsContext: reset called outside of VoiceCallsProvider",
    );
  },
  isLoading: false,
  hasError: false,
});

// Context
const VoiceCallsContext = createContext<VoiceCallsContextType>(
  createDefaultContextValue(),
);

// Provider props
export interface VoiceCallsProviderProps {
  children: ReactNode;
  cacheTimeout?: number; // in milliseconds
  disabled?: boolean; // Disable all fetching for testing
}

// Cache timeout constant
const DEFAULT_CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

export function VoiceCallsProvider({
  children,
  cacheTimeout = DEFAULT_CACHE_TIMEOUT,
  disabled = false,
}: VoiceCallsProviderProps) {
  const { organization, isLoading: isAuthLoading } = useAuth();
  const { toast } = useToast();

  // Initialize state
  const initialState = useMemo(
    () => createDefaultState(organization?.id || null),
    [organization?.id],
  );

  const [state, dispatch] = useReducer(voiceCallsReducer, initialState);

  // Refs for side-effect containment and cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Helper to check if data is stale
  const isDataStale = useCallback(
    (lastFetch: number | null) => {
      if (!lastFetch) return true;
      return Date.now() - lastFetch > cacheTimeout;
    },
    [cacheTimeout],
  );

  // Fetch voice calls
  const refreshCalls = useCallback(async () => {
    if (disabled || !organization?.id || !mountedRef.current) return;

    // Abort previous request if still running
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      dispatch({ type: "CALLS_LOADING", payload: true });

      const authToken = (await supabase.auth.getSession()).data.session
        ?.access_token;

      if (!authToken) {
        throw new Error("No authentication token");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/voice-calls-service?organization_id=${organization.id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          signal: abortControllerRef.current.signal,
        },
      );

      // Check if component is still mounted and request wasn't aborted
      if (!mountedRef.current || abortControllerRef.current?.signal.aborted) {
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const calls = result.data || [];

      if (mountedRef.current) {
        dispatch({ type: "CALLS_SUCCESS", payload: calls });
      }
    } catch (error) {
      if (mountedRef.current && !abortControllerRef.current?.signal.aborted) {
        const errorObj =
          error instanceof Error ? error : new Error("Failed to fetch calls");
        dispatch({ type: "CALLS_ERROR", payload: errorObj });

        toast({
          variant: "destructive",
          title: "Failed to load calls",
          description: errorObj.message,
        });
      }
    }
  }, [disabled, organization?.id, toast]);

  // Fetch voice agents
  const refreshAgents = useCallback(async () => {
    if (disabled || !organization?.id || !mountedRef.current) return;

    try {
      dispatch({ type: "AGENTS_LOADING", payload: true });

      const { data: agentsData, error: agentsError } = await supabase
        .from("voice_agents")
        .select("id, agent_name, title")
        .eq("organization_id", organization.id)
        .eq("archived", false);

      if (!mountedRef.current) return;

      if (agentsError) {
        throw agentsError;
      }

      const agents = agentsData || [];

      if (mountedRef.current) {
        dispatch({ type: "AGENTS_SUCCESS", payload: agents });
      }
    } catch (error) {
      if (mountedRef.current) {
        const errorObj =
          error instanceof Error ? error : new Error("Failed to fetch agents");
        dispatch({ type: "AGENTS_ERROR", payload: errorObj });
      }
    }
  }, [disabled, organization?.id]);

  // Organization change handler
  useEffect(() => {
    dispatch({ type: "SET_ORGANIZATION", payload: organization?.id || null });
  }, [organization?.id]);

  // Initial data loading when auth is ready
  useEffect(() => {
    if (disabled || isAuthLoading || !organization?.id) return;

    let mounted = true;

    const initializeData = async () => {
      try {
        // Load calls if data is stale
        if (isDataStale(state.callsLastFetch)) {
          await refreshCalls();
        }

        // Load agents if data is stale
        if (isDataStale(state.agentsLastFetch)) {
          await refreshAgents();
        }

        if (mounted) {
          dispatch({ type: "SET_INITIALIZED", payload: true });
        }
      } catch (error) {
        console.error("Error initializing voice calls data:", error);
      }
    };

    // Small delay to avoid race conditions with auth
    timeoutRef.current = setTimeout(initializeData, 100);

    return () => {
      mounted = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    disabled,
    isAuthLoading,
    organization?.id,
    isDataStale,
    state.callsLastFetch,
    state.agentsLastFetch,
    refreshCalls,
    refreshAgents,
  ]);

  // Refresh both calls and agents
  const refreshAll = useCallback(async () => {
    await Promise.all([refreshCalls(), refreshAgents()]);
  }, [refreshCalls, refreshAgents]);

  // Invalidation functions
  const invalidateCalls = useCallback(() => {
    dispatch({ type: "INVALIDATE_CALLS" });
  }, []);

  const invalidateAgents = useCallback(() => {
    dispatch({ type: "INVALIDATE_AGENTS" });
  }, []);

  const invalidateAll = useCallback(() => {
    dispatch({ type: "INVALIDATE_ALL" });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  // Computed properties
  const computedValues = useMemo(
    () => ({
      isLoading: state.isLoadingCalls || state.isLoadingAgents,
      hasError: !!(state.callsError || state.agentsError),
    }),
    [
      state.isLoadingCalls,
      state.isLoadingAgents,
      state.callsError,
      state.agentsError,
    ],
  );

  // Stable context value
  const contextValue = useMemo<VoiceCallsContextType>(
    () => ({
      // State
      ...state,

      // Computed
      ...computedValues,

      // Actions
      refreshCalls,
      refreshAgents,
      refreshAll,
      invalidateCalls,
      invalidateAgents,
      invalidateAll,
      reset,
    }),
    [
      state,
      computedValues,
      refreshCalls,
      refreshAgents,
      refreshAll,
      invalidateCalls,
      invalidateAgents,
      invalidateAll,
      reset,
    ],
  );

  return (
    <VoiceCallsContext.Provider value={contextValue}>
      {children}
    </VoiceCallsContext.Provider>
  );
}

// Hook for consuming the context
export function useVoiceCalls(): VoiceCallsContextType {
  const context = useContext(VoiceCallsContext);

  if (context === createDefaultContextValue()) {
    throw new Error(
      "useVoiceCalls must be used within a VoiceCallsProvider. " +
        "Make sure your component tree includes <VoiceCallsProvider> at a higher level.",
    );
  }

  return context;
}

// Selective hooks for specific data
export function useVoiceCallsData() {
  const { calls, isLoadingCalls, callsError, refreshCalls } = useVoiceCalls();
  return useMemo(
    () => ({
      calls,
      isLoading: isLoadingCalls,
      error: callsError,
      refresh: refreshCalls,
    }),
    [calls, isLoadingCalls, callsError, refreshCalls],
  );
}

export function useVoiceAgentsData() {
  const { voiceAgents, isLoadingAgents, agentsError, refreshAgents } =
    useVoiceCalls();
  return useMemo(
    () => ({
      agents: voiceAgents,
      isLoading: isLoadingAgents,
      error: agentsError,
      refresh: refreshAgents,
    }),
    [voiceAgents, isLoadingAgents, agentsError, refreshAgents],
  );
}
